export type CpmOverrideCampaignConfig = {
  cpm_rate_cents: number;
  allow_cpm_override?: boolean | null;
  cpm_override_mode?: string | null;
  cpm_override_presets_cents?: (number | null)[] | null;
  cpm_override_min_cents?: number | null;
  cpm_override_max_cents?: number | null;
};

export type CpmOverrideEditorConfig =
  | { canEdit: false }
  | {
      canEdit: true;
      /** Preset buttons to show ([] when none configured). */
      presetsCents: number[];
      /** Free-value input constraints, or false when only presets are allowed. */
      freeInput: false | { minCents: number; maxCents: number | null };
    };

export type CpmPlatformRateEntry = { platform: string; cpm_rate_cents: number };

/** What the rate rule needs from a campaign: the base rate and its network rates. */
export type CpmRateCampaign = {
  cpm_rate_cents: number | null;
  cpm_platform_rates?: readonly CpmPlatformRateEntry[] | null;
};

export type CpmRateSubmission = {
  platform?: string | null;
  cpm_rate_override_cents?: number | null;
};

const CPM_RATE_PLATFORMS: readonly string[] = ['youtube', 'instagram', 'tiktok'];

const isNetworkRate = (value: unknown): value is number => (
  Number.isSafeInteger(value) && (value as number) > 0
);

function networkRateCents(campaign: CpmRateCampaign, platform: string | null | undefined): number | null {
  if (!platform || !CPM_RATE_PLATFORMS.includes(platform)) return null;
  const entry = (campaign.cpm_platform_rates ?? []).find((rate) => rate?.platform === platform);
  return entry && isNetworkRate(entry.cpm_rate_cents) ? entry.cpm_rate_cents : null;
}

/** A network's rate for this campaign: its own rate, else the base rate. */
export function platformCpmRateCents(campaign: CpmRateCampaign, platform: string): number {
  return networkRateCents(campaign, platform) ?? (campaign.cpm_rate_cents || 0);
}

/**
 * The rate a clip earns: per-clip override, then its network's rate, then the
 * base rate. Mirrors the backend's `effectiveCpmRateCents`; both run the shared
 * `cpmEffectiveRateTestVectors.json`.
 */
export function effectiveCpmRateCents(
  campaign: CpmRateCampaign,
  submission: CpmRateSubmission | null | undefined,
): number {
  const override = submission?.cpm_rate_override_cents;
  if (Number.isInteger(override) && (override as number) >= 0) return override as number;
  return networkRateCents(campaign, submission?.platform) ?? (campaign.cpm_rate_cents || 0);
}

/**
 * The per-clip custom CPM worth calling out on a row: the override, when it
 * differs from what the clip's network would pay anyway. Null otherwise.
 */
export function customCpmRateBadgeCents(
  campaign: CpmRateCampaign,
  submission: CpmRateSubmission,
): number | null {
  const override = submission.cpm_rate_override_cents;
  if (!Number.isInteger(override) || (override as number) < 0) return null;
  return override === platformCpmRateCents(campaign, submission.platform ?? '') ? null : override as number;
}

/**
 * Derive what the audit page's CPM editor should offer. Mirrors the backend's
 * validateCpmOverride constraints for UX only — the backend stays the
 * authority. Platform admins are never constrained: they always get an
 * unbounded free input, with any configured presets kept as shortcuts.
 */
export function getCpmOverrideEditorConfig({
  campaign,
  isPlatformAdmin,
}: {
  campaign: CpmOverrideCampaignConfig | null | undefined;
  isPlatformAdmin: boolean;
}): CpmOverrideEditorConfig {
  if (!campaign) return { canEdit: false };

  const allowed = !!campaign.allow_cpm_override;
  const mode = campaign.cpm_override_mode === 'free' ? 'free' : 'presets';
  const presetsCents = (campaign.cpm_override_presets_cents || []).filter(
    (v): v is number => Number.isInteger(v) && (v as number) >= 0,
  );

  if (isPlatformAdmin) {
    return {
      canEdit: true,
      presetsCents: allowed && mode === 'presets' ? presetsCents : [],
      freeInput: { minCents: 0, maxCents: null },
    };
  }

  if (!allowed) return { canEdit: false };

  if (mode === 'presets') {
    if (presetsCents.length === 0) return { canEdit: false };
    return { canEdit: true, presetsCents, freeInput: false };
  }

  return {
    canEdit: true,
    presetsCents: [],
    freeInput: {
      minCents: campaign.cpm_override_min_cents ?? 0,
      maxCents: campaign.cpm_override_max_cents ?? null,
    },
  };
}
